import { where as liteWhere } from 'firebase/firestore/lite';
import { Users } from '../models/User.tsx';
import { getLiteQueryDocs } from './firestoreLiteQuery.ts';

const FIRESTORE_IN_LIMIT = 10;

export type HierarchyViewer = Pick<Users, 'userName' | 'userType'> &
  Partial<Pick<Users, 'allowSubAgents' | 'parent' | 'master'>>;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const mapUserDoc = (docSnap: { id: string; data: () => unknown }): Users => ({
  id: docSnap.id,
  ...(docSnap.data() as Users),
});

export function canNestSubAgents(
  user: Pick<Users, 'userType' | 'allowSubAgents'> | null | undefined,
): boolean {
  if (!user) return false;
  if (user.userType === 'masterAgent') return true;
  return user.userType === 'subAgent' && user.allowSubAgents === true;
}

export function parentIsRootMaster(
  parentName: string | undefined,
  viewer: Pick<Users, 'userType' | 'userName'> | null | undefined,
  agents: Pick<Users, 'userName' | 'userType'>[] = [],
): boolean {
  if (!parentName) return false;
  const parentRecord = agents.find(agent => agent.userName === parentName);
  if (parentRecord?.userType === 'masterAgent') return true;
  if (parentRecord?.userType === 'subAgent') return false;
  return viewer?.userType === 'masterAgent' && viewer.userName === parentName;
}

export function agentUnderLabel(
  user: Pick<Users, 'userType' | 'parent'>,
  viewer: Pick<Users, 'userType' | 'userName'> | null | undefined,
  agents: Pick<Users, 'userName' | 'userType'>[] = [],
): string | null {
  if (user.userType !== 'subAgent' || !user.parent) return null;
  if (parentIsRootMaster(user.parent, viewer, agents)) return null;
  return `under ${user.parent}`;
}

export function profileRelationLabel(
  user: Pick<Users, 'userType' | 'parent' | 'userName' | 'allowSubAgents'>,
  viewer: Pick<Users, 'userType' | 'userName'> | null | undefined,
  agents: Pick<Users, 'userName' | 'userType' | 'parent'>[] = [],
): string {
  if (user.userType === 'Player') return `Agent ${user.parent ?? ''}`;
  return agentUnderLabel(user, viewer, agents) ?? '';
}

export function visibleChildrenOf(
  parent: Pick<Users, 'userType' | 'allowSubAgents'>,
  children: Users[],
): Users[] {
  const nest = canNestSubAgents(parent);
  return children
    .filter(child => {
      if (!child.userName || child.userType === 'Support') return false;
      if (child.userType === 'Player') return true;
      if (child.userType === 'subAgent') return nest;
      return false;
    })
    .sort((left, right) => {
      const rank = (user: Users) => (user.userType === 'Player' ? 1 : 0);
      const byType = rank(left) - rank(right);
      if (byType !== 0) return byType;
      return left.userName.localeCompare(right.userName, undefined, { numeric: true, sensitivity: 'base' });
    });
}

export async function fetchUsersByParentName(parentUserName: string): Promise<Users[]> {
  if (!parentUserName) return [];
  const docs = await getLiteQueryDocs('users', [
    liteWhere('parent', '==', parentUserName),
  ]);
  return docs
    .map(mapUserDoc)
    .filter(user => user.userType !== 'Support');
}

async function fetchChildrenOfParents(parentNames: string[]): Promise<Users[]> {
  const names = [...new Set(parentNames.filter(Boolean))];
  if (names.length === 0) return [];

  const groups = await Promise.all(
    chunk(names, FIRESTORE_IN_LIMIT).map(part =>
      getLiteQueryDocs('users', [
        part.length === 1
          ? liteWhere('parent', '==', part[0])
          : liteWhere('parent', 'in', part),
      ])
    )
  );

  return groups.flat().map(mapUserDoc).filter(user => user.userType !== 'Support');
}

export async function loadUserByUserName(userName: string): Promise<Users | null> {
  if (!userName) return null;
  const docs = await getLiteQueryDocs('users', [
    liteWhere('userName', '==', userName),
  ]);
  if (docs.length === 0) return null;
  return mapUserDoc(docs[0]);
}

export async function resolveRootMasterName(start: HierarchyViewer): Promise<string> {
  if (start.userType === 'masterAgent') return start.userName;
  if (start.userType === 'Player' && start.master) return start.master;

  const visited = new Set<string>();
  let currentName = start.userName;

  while (currentName) {
    const key = currentName.trim().toUpperCase();
    if (visited.has(key)) break;
    visited.add(key);

    const user = await loadUserByUserName(currentName);
    if (!user) break;
    if (user.userType === 'masterAgent') return user.userName;
    if (!user.parent) break;
    currentName = user.parent;
  }

  return start.master || start.parent || start.userName;
}

export async function isInAgentSubtree(target: Users, agentUserName: string): Promise<boolean> {
  if (!agentUserName) return false;
  if (target.userName?.trim().toUpperCase() === agentUserName.trim().toUpperCase()) return true;

  const visited = new Set<string>();
  let parentName = target.parent;

  while (parentName) {
    const key = parentName.trim().toUpperCase();
    if (visited.has(key)) return false;
    visited.add(key);
    if (key === agentUserName.trim().toUpperCase()) return true;

    const parent = await loadUserByUserName(parentName);
    if (!parent) return false;
    if (parent.userType === 'masterAgent') {
      return parent.userName.trim().toUpperCase() === agentUserName.trim().toUpperCase();
    }
    parentName = parent.parent;
  }

  return false;
}

export async function collectDescendantRoster(viewer: HierarchyViewer): Promise<{
  subAgents: Users[];
  players: Users[];
  agentScope: string[];
}> {
  let root: Users = {
    userName: viewer.userName,
    userType: viewer.userType,
    allowSubAgents: viewer.allowSubAgents,
  } as Users;

  if (viewer.userType === 'subAgent' && viewer.allowSubAgents === undefined) {
    const loaded = await loadUserByUserName(viewer.userName);
    if (loaded) root = loaded;
  }

  const subAgents: Users[] = [];
  const players: Users[] = [];
  const agentScope = [root.userName];
  const visited = new Set<string>([root.userName]);
  let frontier: Users[] = [root];

  while (frontier.length > 0) {
    const parents = frontier;
    frontier = [];
    const children = await fetchChildrenOfParents(parents.map(parent => parent.userName));
    const byParent = new Map<string, Users[]>();

    children.forEach(child => {
      if (!child.parent) return;
      const list = byParent.get(child.parent) ?? [];
      list.push(child);
      byParent.set(child.parent, list);
    });

    parents.forEach(parent => {
      visibleChildrenOf(parent, byParent.get(parent.userName) ?? []).forEach(child => {
        if (!child.userName || visited.has(child.userName)) return;
        visited.add(child.userName);
        if (child.userType === 'Player') {
          players.push(child);
          return;
        }
        if (child.userType === 'subAgent') {
          subAgents.push(child);
          agentScope.push(child.userName);
          frontier.push(child);
        }
      });
    });
  }

  return { subAgents, players, agentScope };
}

export type UserBranch<T> = {
  user: Users;
  players: T[];
  children: UserBranch<T>[];
  hasSubAgents: boolean;
};

export function buildUserBranches<T extends { parent?: string }>(
  root: Users,
  agents: Users[],
  players: T[],
): UserBranch<T> {
  const agentsByParent = new Map<string, Users[]>();
  agents.forEach(agent => {
    if (agent.userType !== 'subAgent' || !agent.parent || !agent.userName) return;
    const list = agentsByParent.get(agent.parent) ?? [];
    list.push(agent);
    agentsByParent.set(agent.parent, list);
  });

  const playersByParent = new Map<string, T[]>();
  players.forEach(player => {
    if (!player.parent) return;
    const list = playersByParent.get(player.parent) ?? [];
    list.push(player);
    playersByParent.set(player.parent, list);
  });

  const visited = new Set<string>();

  const walk = (user: Users): UserBranch<T> => {
    visited.add(user.userName);
    const childAgents = canNestSubAgents(user)
      ? (agentsByParent.get(user.userName) ?? [])
        .filter(agent => agent.userName && !visited.has(agent.userName))
        .sort((left, right) => left.userName.localeCompare(right.userName, undefined, { numeric: true, sensitivity: 'base' }))
      : [];
    const children = childAgents.map(walk);
    return {
      user,
      players: playersByParent.get(user.userName) ?? [],
      children,
      hasSubAgents: children.length > 0,
    };
  };

  return walk(root);
}

export function pruneBranches<T>(branch: UserBranch<T>): UserBranch<T> {
  const children = branch.children
    .filter(child => child.players.length > 0 || child.children.some(hasAnyPlayers))
    .map(child => pruneBranches(child));
  return {
    ...branch,
    children,
    hasSubAgents: children.length > 0,
  };
}

function hasAnyPlayers<T>(branch: UserBranch<T>): boolean {
  return branch.players.length > 0 || branch.children.some(hasAnyPlayers);
}

export type HierarchySection<T> = {
  key: string;
  title: string;
  meta: string | null;
  depth: number;
  players: T[];
};

export function listHierarchySections<T extends { parent?: string; id?: string; userName?: string }>(
  root: Users,
  agents: Users[],
  players: T[],
): HierarchySection<T>[] {
  const tree = pruneBranches(buildUserBranches(root, agents, players));
  const placed = new Set<string>();
  const sections: HierarchySection<T>[] = [];

  const tokenOf = (player: T) => player.id || player.userName || '';

  const remember = (list: T[]) => {
    list.forEach(player => {
      const token = tokenOf(player);
      if (token) placed.add(token);
    });
  };

  const walk = (branch: UserBranch<T>, depth: number, parentName?: string, parentIsMaster = false) => {
    remember(branch.players);
    sections.push({
      key: `${depth}:${branch.user.userName}`,
      title: branch.user.userName,
      meta: depth > 0 && parentName && !parentIsMaster ? `Agent under ${parentName}` : null,
      depth,
      players: branch.players,
    });
    const branchIsMaster = branch.user.userType === 'masterAgent';
    branch.children.forEach(child => walk(child, depth + 1, branch.user.userName, branchIsMaster));
  };

  if (tree.players.length > 0) walk(tree, 0);
  else tree.children.forEach(child => walk(child, 1, root.userName, root.userType === 'masterAgent'));

  const orphanGroups = new Map<string, T[]>();
  players.forEach(player => {
    const token = tokenOf(player);
    if (token && placed.has(token)) return;
    const parentName = player.parent || 'Unassigned';
    const list = orphanGroups.get(parentName) ?? [];
    list.push(player);
    orphanGroups.set(parentName, list);
  });

  orphanGroups.forEach((group, parentName) => {
    sections.push({
      key: `orphan:${parentName}`,
      title: parentName,
      meta: null,
      depth: 1,
      players: group,
    });
  });

  return sections;
}
